import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Stethoscope, Glasses, Megaphone, Play, CheckCircle2, Monitor, CalendarDays, Printer, XCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import PatientSearchHeader from "@/components/PatientSearchHeader";

const STATUS_PILL = {
  waiting: "border-blue-200 bg-blue-50 text-blue-700",
  called: "border-violet-200 bg-violet-50 text-violet-700",
  in_consultation: "border-amber-200 bg-amber-50 text-amber-700",
  completed: "border-emerald-200 bg-emerald-50 text-emerald-700",
  cancelled: "border-rose-200 bg-rose-50 text-rose-700",
};

export default function OpdQueue() {
  const { branch, user } = useAuth();
  const canManageQueue = ["hospital_admin", "branch_admin", "super_admin"].includes(user?.role);
  const [items, setItems] = useState([]);
  // Selected range drives the queue endpoint. When both empty → live today.
  // When range spans multiple days → aggregated read-only view (no auto-refresh).
  const [date, setDate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromDate && toDate) { params.from_date = fromDate; params.to_date = toDate; }
      const { data } = await api.get("/patients/visits/queue/today", { params });
      setItems(data.items);
      setDate(data.date);
    } finally { setLoading(false); }
  };
  // Auto-refresh only on the live "today" view — a manual range shouldn't
  // spam the API while the receptionist is browsing history.
  useEffect(() => {
    load();
    if (!fromDate && !toDate) {
      const i = setInterval(load, 20000);
      return () => clearInterval(i);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  const changeStatus = async (v, status) => {
    try {
      await api.patch(`/patients/visits/${v.id}/status`, { status });
      const labels = { called: "Patient called", in_consultation: "Consultation started", completed: "Visit completed" };
      toast.success(labels[status] || "Status updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const cancelVisit = async (v) => {
    const reason = window.prompt(`Cancel token #${v.token_no} for ${v.patient_name}?\nEnter a reason:`, "");
    if (reason === null) return;
    try {
      await api.post(`/patients/visits/${v.id}/cancel`, { reason });
      toast.success(`Visit cancelled${reason ? ` — ${reason}` : ""}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const deleteVisit = async (v) => {
    if (!window.confirm(`Delete token #${v.token_no} (${v.patient_name})? This cannot be undone.\n\nOnly works if the visit has no consultation, optometry or invoice attached.`)) return;
    try {
      await api.delete(`/patients/visits/${v.id}`);
      toast.success(`Visit deleted`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="section-label">{(fromDate && toDate) ? `Queue · ${date}` : `Live queue · ${date}`}</div>
          <h1 className="font-heading text-3xl font-semibold">OPD queue & tokens</h1>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {/* From/To date range — leave both blank for today's live queue.
              Same day both sides = single-day view; different days = span. */}
          <div className="flex items-end gap-2 rounded-md border border-border bg-secondary/40 px-2 py-1">
            <CalendarDays size={14} className="mb-1 text-ink-muted" />
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">From</div>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                max={toDate || new Date().toISOString().slice(0, 10)}
                className="rounded border border-border bg-white px-2 py-0.5 text-xs"
                data-testid="opd-queue-from-date"
              />
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">To</div>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                min={fromDate || undefined}
                max={new Date().toISOString().slice(0, 10)}
                className="rounded border border-border bg-white px-2 py-0.5 text-xs"
                data-testid="opd-queue-to-date"
              />
            </div>
            {(fromDate || toDate) && (
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => { setFromDate(""); setToDate(""); }} data-testid="opd-queue-today-btn">
                Today
              </Button>
            )}
          </div>
          <Button variant="outline" onClick={() => load()} data-testid="opd-refresh-btn"><RefreshCw size={14} className="mr-2" />Refresh</Button>
          {branch?.id && (
            <a href={`/board/${branch.id}`} target="_blank" rel="noreferrer">
              <Button variant="outline" data-testid="opd-open-board-btn"><Monitor size={14} className="mr-2" />Open display board</Button>
            </a>
          )}
        </div>
      </div>

      <PatientSearchHeader
        label="OR SEARCH ANY PATIENT (across all branches)"
        placeholder="Type name, UHID or mobile (min 2 chars)…"
        crossBranch
        testIdRoot="opd-queue-patient-search"
      />

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-4 py-3 text-left">Token</th>
                <th className="px-4 py-3 text-left">Patient</th>
                <th className="px-4 py-3 text-left">UHID</th>
                <th className="px-4 py-3 text-left">Doctor</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && <tr><td colSpan="6" className="px-4 py-6 text-center text-ink-muted">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-4 py-10 text-center text-ink-muted">
                    {(fromDate && toDate) ? `No visits from ${fromDate} to ${toDate}.` : "No visits today yet."}
                  </td>
                </tr>
              )}
              {items.map(v => (
                <tr key={v.id} className="row-enter hover:bg-secondary/40" data-testid={`opd-row-${v.token_no}`}>
                  <td className="px-4 py-3 font-heading text-lg text-brand">#{v.token_no}</td>
                  <td className="px-4 py-3 font-medium">{v.patient_name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{v.patient_uhid}</td>
                  <td className="px-4 py-3 text-xs">{v.doctor_name ? `Dr. ${v.doctor_name}` : "—"}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={STATUS_PILL[v.status] || ""}>{v.status.replace("_"," ")}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex flex-wrap items-center justify-end gap-1.5">
                      {v.status === "waiting" && (
                        <Button size="sm" variant="outline" onClick={() => changeStatus(v, "called")} title="Call patient" data-testid={`opd-call-${v.token_no}`}>
                          <Megaphone size={12} className="mr-1 text-violet-600" />Call
                        </Button>
                      )}
                      {(v.status === "waiting" || v.status === "called") && (
                        <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => changeStatus(v, "in_consultation")} data-testid={`opd-start-${v.token_no}`}>
                          <Play size={12} className="mr-1" />Start consult
                        </Button>
                      )}
                      <Link to={`/app/opd/consult/${v.id}`}>
                        <Button size="sm" className="bg-brand hover:bg-brand-700" data-testid={`opd-consult-${v.token_no}`}>
                          <Stethoscope size={12} className="mr-1" />{v.status === "completed" ? "View" : "Consult"}
                        </Button>
                      </Link>
                      <Link to={`/app/optometry?visit=${v.id}`}>
                        <Button size="sm" variant="outline"><Glasses size={12} className="mr-1" />Optometry</Button>
                      </Link>
                      {/* Reprint the doctor's Rx for a completed visit —
                          useful when the receptionist needs to hand over a
                          copy of a past prescription. */}
                      {v.status === "completed" && (
                        <a href={`/print/consultation/${v.id}`} target="_blank" rel="noreferrer">
                          <Button size="sm" variant="outline" title="Reprint prescription" data-testid={`opd-reprint-${v.token_no}`}>
                            <Printer size={12} className="mr-1" />Rx
                          </Button>
                        </a>
                      )}
                      {v.status === "in_consultation" && (
                        <Button size="sm" variant="ghost" onClick={() => changeStatus(v, "completed")} data-testid={`opd-complete-${v.token_no}`}>
                          <CheckCircle2 size={12} className="mr-1 text-emerald-600" />Mark done
                        </Button>
                      )}
                      {canManageQueue && v.status !== "cancelled" && v.status !== "completed" && (
                        <Button size="sm" variant="ghost" onClick={() => cancelVisit(v)} title="Cancel visit" data-testid={`opd-cancel-${v.token_no}`}>
                          <XCircle size={12} className="mr-1 text-rose-600" />Cancel
                        </Button>
                      )}
                      {canManageQueue && (
                        <Button size="sm" variant="ghost" onClick={() => deleteVisit(v)} title="Delete visit (only if empty)" data-testid={`opd-delete-${v.token_no}`}>
                          <Trash2 size={12} className="text-rose-700" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
